import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Table, Card, Button, Form, Modal, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaEdit, FaSave, FaTimes, FaEye } from 'react-icons/fa';

const TarifariosEmpresasTable = ({
    data,
    loading,
    total,
    pageSize,
    pageNumber,
    onPageChange,
    onPageSizeChange,
    permisos = {},
    children,
    updating,
    onPrecioUpdated,
    updatePreciosMasivo,
    clearUpdateError
}) => {
    const user = useSelector(state => state.auth.user);

    // Estados para el modal de edición individual
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPrecio, setSelectedPrecio] = useState(null);
    const [editPrecio, setEditPrecio] = useState('');

    // Estados para edición masiva
    const [selectedPrecios, setSelectedPrecios] = useState(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [bulkPrecio, setBulkPrecio] = useState('');

    // Formatear monto con separadores de miles
    const formatMonto = (monto) => {
        if (!monto && monto !== 0) return '-';
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(monto);
    };

    // Formatear fecha
    const formatFecha = (fecha) => {
        if (!fecha) return '-';
        try {
            return new Date(fecha).toLocaleDateString('es-CL');
        } catch {
            return '-';
        }
    };

    // Funciones para manejo de selección masiva
    const handleSelectPrecio = (precioId) => {
        setSelectedPrecios(prev => {
            const newSet = new Set(prev);
            if (newSet.has(precioId)) {
                newSet.delete(precioId);
            } else {
                newSet.add(precioId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = new Set(data.map(precio => precio.id));
            setSelectedPrecios(allIds);
        } else {
            setSelectedPrecios(new Set());
        }
    };

    // Limpiar selección cuando cambien los datos
    useEffect(() => {
        setSelectedPrecios(new Set());
    }, [data, pageNumber]);

    // Función para abrir modal de edición individual
    const handleRowClick = (precio) => {
        if (!permisos.canEdit) return;
        
        setSelectedPrecio(precio);
        setEditPrecio(precio.precio?.toString() || '');
        setShowEditModal(true);
    };

    const handleOpenBulkEdit = () => {
        if (selectedPrecios.size === 0) return;
        setBulkPrecio('');
        setShowBulkEditModal(true);
    };

    const handleCloseBulkEdit = () => {
        setShowBulkEditModal(false);
        setBulkPrecio('');
        if (clearUpdateError) {
            clearUpdateError();
        }
    };

    // Función para cerrar modal individual
    const handleCloseModal = () => {
        setShowEditModal(false);
        setSelectedPrecio(null);
        setEditPrecio('');
        if (clearUpdateError) {
            clearUpdateError();
        }
    };

    // Función para guardar cambios individuales
    const handleSaveChanges = async () => {
        if (!selectedPrecio || !editPrecio) return;

        const nuevoPrecio = parseFloat(editPrecio);
        if (isNaN(nuevoPrecio) || nuevoPrecio <= 0) {
            alert('Por favor ingrese un precio válido mayor a 0');
            return;
        }

        const precioActualizado = {
            id: selectedPrecio.id,
            precio: nuevoPrecio
        };

        try {
            const result = await updatePreciosMasivo([precioActualizado]);
            if (result.success) {
                handleCloseModal();
                if (onPrecioUpdated) {
                    onPrecioUpdated();
                }
            }
        } catch (error) {
            console.error('Error al actualizar precio:', error);
        }
    };

    // Función para guardar cambios masivos
    const handleSaveBulkChanges = async () => {
        if (selectedPrecios.size === 0 || !bulkPrecio) return;

        const nuevoPrecio = parseFloat(bulkPrecio);
        if (isNaN(nuevoPrecio) || nuevoPrecio <= 0) {
            alert('Por favor ingrese un precio válido mayor a 0');
            return;
        }

        const preciosActualizados = Array.from(selectedPrecios).map(id => ({
            id: id,
            precio: nuevoPrecio
        }));

        try {
            const result = await updatePreciosMasivo(preciosActualizados);
            if (result.success) {
                setSelectedPrecios(new Set());
                handleCloseBulkEdit();
                if (onPrecioUpdated) {
                    onPrecioUpdated();
                }
            }
        } catch (error) {
            console.error('Error al actualizar precios masivo:', error);
        }
    };

    // Definir columnas
    const columns = [
        {
            Header: (
                <Form.Check
                    type="checkbox"
                    checked={data.length > 0 && selectedPrecios.size === data.length}
                    onChange={handleSelectAll}
                    disabled={!permisos.canEdit}
                />
            ),
            accessor: (row) => (
                <Form.Check
                    type="checkbox"
                    checked={selectedPrecios.has(row.id)}
                    onChange={() => handleSelectPrecio(row.id)}
                    disabled={!permisos.canEdit}
                />
            )
        },
        {
            Header: 'Empresa',
            accessor: (row) => (
                <div>
                    <div className="fw-bold">{row.empresa?.sigla || '-'}</div>
                    <small className="text-muted">{row.empresa?.razon_social || '-'}</small>
                </div>
            )
        },
        {
            Header: 'Servicio',
            accessor: (row) => row.servicio_spread?.servicio || '-'
        },
        {
            Header: 'Región',
            accessor: (row) => row.comuna?.region?.region || '-'
        },
        {
            Header: 'Comuna',
            accessor: (row) => row.comuna?.comuna || '-'
        },
        {
            Header: 'Precio',
            accessor: (row) => (
                <div className="fw-bold text-success">
                    {formatMonto(row.precio)}
                </div>
            )
        },
        {
            Header: 'Vigencia',
            accessor: (row) => (
                <div>
                    <div>
                        <small className="text-muted">Desde:</small> {formatFecha(row.fecha_inicio)}
                    </div>
                    <div>
                        <small className="text-muted">Hasta:</small> {formatFecha(row.fecha_fin)}
                    </div>
                </div>
            )
        },
        {
            Header: 'Creado',
            accessor: (row) => (
                <div>
                    <div className="fw-bold">{row.createdBy || '-'}</div>
                    <small className="text-muted">{formatFecha(row.createdDate)}</small>
                </div>
            )
        }
    ];

    // Renderizar cabecera
    const renderHeader = () => (
        <tr>
            {columns.map((col, idx) => (
                <th key={idx}>{col.Header}</th>
            ))}
        </tr>
    );

    // Renderizar filas
    const renderRows = () => (
        data.map((row, idx) => (
            <tr 
                key={row.id || idx}
                style={{ 
                    cursor: permisos.canEdit ? 'pointer' : 'default'
                }}
                onMouseEnter={(e) => {
                    if (permisos.canEdit) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                }}
            >
                {columns.map((col, cidx) => (
                    <td 
                        key={cidx}
                        onClick={cidx === 0 && permisos.canEdit ? 
                            (e) => e.stopPropagation() : 
                            () => handleRowClick(row)
                        }
                        style={{
                            cursor: cidx === 0 && permisos.canEdit ? 'default' : 'inherit'
                        }}
                    >
                        {col.accessor(row)}
                    </td>
                ))}
            </tr>
        ))
    );

    // Paginación
    const pageIndex = Math.max(0, (pageNumber || 1) - 1);
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    const renderPagination = () => (
        <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
                <span className="text-muted">
                    Mostrando {Math.min((pageIndex * pageSize) + 1, total)} a {Math.min((pageIndex + 1) * pageSize, total)} de {total} registros
                </span>
            </div>
            <div className="d-flex align-items-center gap-2">
                <Form.Select
                    size="sm"
                    value={pageSize}
                    onChange={e => onPageSizeChange(parseInt(e.target.value))}
                    style={{ width: 'auto' }}
                >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </Form.Select>
                <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={pageNumber <= 1}
                    onClick={() => onPageChange(pageNumber - 1)}
                >
                    Anterior
                </Button>
                <span className="text-muted">
                    Página {pageNumber} de {pageCount}
                </span>
                <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={pageNumber >= pageCount}
                    onClick={() => onPageChange(pageNumber + 1)}
                >
                    Siguiente
                </Button>
            </div>
        </div>
    );

    // Obtener precios seleccionados con su información completa
    const getSelectedPreciosData = () => {
        return data.filter(precio => selectedPrecios.has(precio.id));
    };

    return (
        <>
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-0">Precios de Empresas</h5>
                            <small className="text-muted">
                                {total} registros encontrados
                            </small>
                        </div>
                        <div className="d-flex gap-2">
                            {children}
                            {permisos.canEdit && selectedPrecios.size > 0 && (
                                <Button
                                    variant="warning"
                                    onClick={handleOpenBulkEdit}
                                    disabled={updating}
                                >
                                    <FaEdit className="me-2" />
                                    Editar Seleccionados
                                    <Badge bg="light" text="dark" className="ms-2">
                                        {selectedPrecios.size}
                                    </Badge>
                                </Button>
                            )}
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </Spinner>
                        </div>
                    ) : data.length === 0 ? (
                        <div className="text-center p-4">
                            <p className="text-muted mb-0">No se encontraron precios de empresas</p>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <Table responsive hover className="mb-0">
                                <thead className="table-light">
                                    {renderHeader()}
                                </thead>
                                <tbody>
                                    {renderRows()}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Paginación */}
            {!loading && data.length > 0 && renderPagination()}

            {/* Modal de edición individual */}
            <Modal show={showEditModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaEdit className="me-2" />
                        Editar Precio de Empresa
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedPrecio && (
                        <>
                            <div className="bg-light p-3 rounded mb-3">
                                <div className="row">
                                    <div className="col-md-6">
                                        <strong>Empresa:</strong><br />
                                        {selectedPrecio.empresa?.sigla} - {selectedPrecio.empresa?.razon_social}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Servicio:</strong><br />
                                        {selectedPrecio.servicio_spread?.servicio}
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-6">
                                        <strong>Región:</strong><br />
                                        {selectedPrecio.comuna?.region?.region}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Comuna:</strong><br />
                                        {selectedPrecio.comuna?.comuna}
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-12">
                                        <strong>Precio Actual:</strong><br />
                                        <span className="text-success fw-bold">
                                            {formatMonto(selectedPrecio.precio)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Nuevo Precio *</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={editPrecio}
                                    onChange={(e) => setEditPrecio(e.target.value)}
                                    placeholder="Ingrese el nuevo precio"
                                    min="1"
                                    step="1"
                                />
                                <Form.Text className="text-muted">
                                    Ingrese solo números. El precio debe ser mayor a 0.
                                </Form.Text>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        <FaTimes className="me-1" />
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveChanges}
                        disabled={updating || !editPrecio}
                    >
                        {updating ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                        ) : (
                            <FaSave className="me-2" />
                        )}
                        Guardar Cambios
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de edición masiva */}
            <Modal show={showBulkEditModal} onHide={handleCloseBulkEdit} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaEdit className="me-2" />
                        Editar Precios Masivamente
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info">
                        <FaEye className="me-2" />
                        Vas a modificar el precio de <strong>{selectedPrecios.size}</strong> registros seleccionados.
                    </Alert>

                    <Form.Group className="mb-3">
                        <Form.Label>Nuevo Precio para Todos los Seleccionados *</Form.Label>
                        <Form.Control
                            type="number"
                            value={bulkPrecio}
                            onChange={(e) => setBulkPrecio(e.target.value)}
                            placeholder="Ingrese el precio que se aplicará a todos"
                            min="1"
                            step="1"
                        />
                        <Form.Text className="text-muted">
                            Este precio se aplicará a todos los {selectedPrecios.size} registros seleccionados.
                        </Form.Text>
                    </Form.Group>

                    {/* Vista previa de registros seleccionados */}
                    <div className="bg-light p-3 rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <h6>Registros que serán modificados:</h6>
                        {getSelectedPreciosData().map((precio, idx) => (
                            <div key={precio.id} className="border-bottom py-2">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <strong>{precio.empresa?.sigla}</strong> - {precio.servicio_spread?.servicio}
                                        <br />
                                        <small className="text-muted">
                                            {precio.comuna?.region?.region} / {precio.comuna?.comuna}
                                        </small>
                                    </div>
                                    <div className="text-end">
                                        <div className="text-muted">Precio actual:</div>
                                        <div className="fw-bold">{formatMonto(precio.precio)}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseBulkEdit}>
                        <FaTimes className="me-1" />
                        Cancelar
                    </Button>
                    <Button 
                        variant="warning" 
                        onClick={handleSaveBulkChanges}
                        disabled={updating || !bulkPrecio || selectedPrecios.size === 0}
                    >
                        {updating ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                        ) : (
                            <FaSave className="me-2" />
                        )}
                        Actualizar {selectedPrecios.size} Precios
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

TarifariosEmpresasTable.propTypes = {
    data: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    total: PropTypes.number,
    pageSize: PropTypes.number,
    pageNumber: PropTypes.number,
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func,
    permisos: PropTypes.object,
    children: PropTypes.node,
    updating: PropTypes.bool,
    onPrecioUpdated: PropTypes.func,
    updatePreciosMasivo: PropTypes.func,
    clearUpdateError: PropTypes.func
};

export default TarifariosEmpresasTable;
